import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { User, Mail, Phone, Calendar, MessageSquare } from "lucide-react";
import { FaLinkedin } from "react-icons/fa";
import { getFirstEmail, getAllEmails } from "../../utils/emailHelpers";

export const ProspectItem = ({ prospect, selected, onSelect, onNavigate }) => {
  if (!prospect || !prospect.individual) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mb-2">
        <p className="text-sm text-gray-500">Prospect non disponible</p>
      </div>
    );
  }

  const individual = prospect.individual;
  const lastHistoryEntry = prospect.stepHistory && prospect.stepHistory.length > 0 
    ? prospect.stepHistory[prospect.stepHistory.length - 1] 
    : null;

  const handleClick = (e) => {
    // Ne pas naviguer si on clique sur la checkbox ou un lien LinkedIn
    if (e.target.type === 'checkbox' || e.target.closest('a')) {
      return;
    }
    if (onNavigate) {
      onNavigate(individual._id);
    }
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(prospect._id);
    }
  };

  return (
    <div 
      className={`bg-white border rounded-lg p-2 mb-2 shadow-sm transition-all duration-200 cursor-pointer ${
        selected 
          ? 'border-blue-300 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:shadow-md hover:border-blue-200'
      }`}
      onClick={handleClick}
    >
      {/* Header avec checkbox, nom et photo */}
      <div className="flex items-center space-x-2 mb-1">
        <input
          type="checkbox"
          checked={selected || false}
          onChange={handleCheckboxClick}
          onClick={(e) => e.stopPropagation()} // Empêcher la propagation du clic
          className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer"
        />
        {individual.photo ? (
          <img 
            src={individual.photo} 
            alt={individual.Name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
            <User size={12} className="text-indigo-600" />
          </div>
        )}
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <h4 className="text-xs font-medium text-gray-900 truncate hover:text-blue-600 m-0">
            {individual.Name || "Nom non disponible"}
          </h4>
          {individual.linkedinUrl && (
            <a
              href={individual.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
              title="Voir le profil LinkedIn"
              onClick={(e) => e.stopPropagation()}
            >
              <FaLinkedin size={14} />
            </a>
          )}
        </div>
        {lastHistoryEntry && lastHistoryEntry.notes && (
          <div className="flex items-center text-blue-600" title={lastHistoryEntry.notes}>
            <MessageSquare size={10} />
          </div>
        )}
      </div>

      {/* Informations de contact compactes */}
      <div className="space-y-0.5">
        {/* Email professionnel */}
        {getAllEmails(individual.professionalEmail).length > 0 && (
          <div className="flex items-center space-x-1 text-xs text-gray-600">
            <Mail size={10} className="text-blue-500 flex-shrink-0" />
            <span className="truncate">{getFirstEmail(individual.professionalEmail)}</span>
          </div>
        )}
        {/* Email personnel */}
        {getAllEmails(individual.personalEmail).length > 0 && (
          <div className="flex items-center space-x-1 text-xs text-gray-600">
            <Mail size={10} className="text-green-500 flex-shrink-0" />
            <span className="truncate">{getFirstEmail(individual.personalEmail)}</span>
          </div>
        )}
        {/* Téléphone */}
        {individual.phoneNumber && (
          <div className="flex items-center space-x-1 text-xs text-gray-600">
            <Phone size={10} className="text-gray-500 flex-shrink-0" />
            <span>{individual.phoneNumber}</span>
          </div>
        )}
      </div>

      {/* Date de déplacement à cette étape */}
      <div className="flex items-center space-x-1 text-xs text-gray-400 mt-1">
        <Calendar size={10} />
        <span>
          {lastHistoryEntry && lastHistoryEntry.date
            ? `Déplacé le ${new Date(lastHistoryEntry.date).toLocaleDateString('fr-FR')}`
            : `Ajouté le ${new Date(prospect.addedAt).toLocaleDateString('fr-FR')}`
          }
        </span>
      </div>
    </div>
  );
};

export default function SortableProspectItem({ id, prospect, containerId, selected, onSelect, onNavigate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    data: {
      type: '.prospect',
      prospect,
      containerId,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProspectItem 
        prospect={prospect} 
        selected={selected}
        onSelect={onSelect}
        onNavigate={onNavigate}
      />
    </div>
  );
}